type RoomSessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface StoredRoomSession {
  token: string | null;
  participantId: string | null;
  participantName: string | null;
  participantRole: string | null;
  participantIsHost: boolean;
  roomIsCreator: boolean;
  participantIsInvited: boolean;
  participantProfileId: string | null;
}

interface JoinedRoomParticipant {
  id?: unknown;
  displayName?: unknown;
  role?: unknown;
  profileId?: unknown;
}

export interface JoinedRoomSessionResponse {
  token?: unknown;
  participant?: JoinedRoomParticipant | null;
  isHost?: unknown;
  isInvited?: unknown;
}

export interface RoomAccessSessionResponse {
  displayName?: unknown;
  profileId?: unknown;
  isHost?: unknown;
  isInvited?: unknown;
}

function storageOrThrow(storage?: RoomSessionStorage): RoomSessionStorage {
  if (storage) return storage;
  return window.sessionStorage;
}

export function getRoomSessionKeys(roomCode: string) {
  return {
    token: `room-token-${roomCode}`,
    participantId: `participant-id-${roomCode}`,
    participantName: `participant-name-${roomCode}`,
    participantRole: `participant-role-${roomCode}`,
    participantIsHost: `participant-isHost-${roomCode}`,
    roomIsCreator: `room-is-creator-${roomCode}`,
    participantIsInvited: `participant-isInvited-${roomCode}`,
    participantProfileId: `participant-profileId-${roomCode}`,
  };
}

export function getStoredRoomSession(roomCode: string, storage?: RoomSessionStorage): StoredRoomSession {
  const sessionStorage = storageOrThrow(storage);
  const keys = getRoomSessionKeys(roomCode);

  return {
    token: sessionStorage.getItem(keys.token),
    participantId: sessionStorage.getItem(keys.participantId),
    participantName: sessionStorage.getItem(keys.participantName),
    participantRole: sessionStorage.getItem(keys.participantRole),
    participantIsHost: sessionStorage.getItem(keys.participantIsHost) === 'true',
    roomIsCreator: sessionStorage.getItem(keys.roomIsCreator) === 'true',
    participantIsInvited: sessionStorage.getItem(keys.participantIsInvited) === 'true',
    participantProfileId: sessionStorage.getItem(keys.participantProfileId),
  };
}

export function hasReusableStoredRoomSession(session: StoredRoomSession) {
  return Boolean(session.token && session.participantId);
}

export function clearStoredRoomSession(roomCode: string, storage?: RoomSessionStorage) {
  const sessionStorage = storageOrThrow(storage);
  Object.values(getRoomSessionKeys(roomCode)).forEach((key) => sessionStorage.removeItem(key));
}

function requireString(value: unknown, fieldName: string) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Cannot store room session without ${fieldName}`);
  }
  return value;
}

function requireParticipantRole(value: unknown) {
  if (value === 'model' || value === 'artist') return value;
  throw new Error('Cannot store room session without participant.role');
}

export function storeJoinedRoomSession(
  roomCode: string,
  data: JoinedRoomSessionResponse,
  storage?: RoomSessionStorage
) {
  const sessionStorage = storageOrThrow(storage);
  const keys = getRoomSessionKeys(roomCode);
  const participant = data.participant;

  const token = requireString(data.token, 'token');
  const participantId = requireString(participant?.id, 'participant.id');
  const participantName = requireString(participant?.displayName, 'participant.displayName');
  const participantRole = requireParticipantRole(participant?.role);
  const isHost = data.isHost === true;

  sessionStorage.setItem(keys.token, token);
  sessionStorage.setItem(keys.participantId, participantId);
  sessionStorage.setItem(keys.participantName, participantName);
  sessionStorage.setItem(keys.participantRole, participantRole);
  sessionStorage.setItem(keys.participantIsHost, isHost ? 'true' : 'false');
  sessionStorage.setItem(keys.roomIsCreator, isHost ? 'true' : 'false');
  sessionStorage.setItem(keys.participantIsInvited, data.isInvited === true ? 'true' : 'false');

  if (typeof participant?.profileId === 'string' && participant.profileId.length > 0) {
    sessionStorage.setItem(keys.participantProfileId, participant.profileId);
  } else {
    sessionStorage.removeItem(keys.participantProfileId);
  }
}

export function updateStoredRoomSessionFromAccess(
  roomCode: string,
  data: RoomAccessSessionResponse,
  storage?: RoomSessionStorage
) {
  const sessionStorage = storageOrThrow(storage);
  const keys = getRoomSessionKeys(roomCode);

  if (typeof data.displayName === 'string' && data.displayName.trim()) {
    sessionStorage.setItem(keys.participantName, data.displayName);
  }

  if (typeof data.profileId === 'string' && data.profileId.length > 0) {
    sessionStorage.setItem(keys.participantProfileId, data.profileId);
  } else if (data.profileId === null) {
    sessionStorage.removeItem(keys.participantProfileId);
  }

  if (typeof data.isHost === 'boolean') {
    sessionStorage.setItem(keys.participantIsHost, data.isHost ? 'true' : 'false');
    sessionStorage.setItem(keys.roomIsCreator, data.isHost ? 'true' : 'false');
  }

  if (typeof data.isInvited === 'boolean') {
    sessionStorage.setItem(keys.participantIsInvited, data.isInvited ? 'true' : 'false');
  }
}

export function getRoomSessionDiagnosticContext(roomCode: string, storage?: RoomSessionStorage) {
  const session = getStoredRoomSession(roomCode, storage);
  return {
    roomCode,
    participantId: session.participantId,
    participantName: session.participantName,
    participantRole: session.participantRole,
  };
}
